import React, {
  RefObject,
  useCallback,
  useEffect,
  useState,
} from 'react';
import isNil from 'lodash/isNil';
import List from 'views/EMR/Settings/Payers/List';
import CreateInsuranceAddressDialog, {
  PayerConfigDialogParams,
} from 'views/EMR/Settings/Payers/Dialog';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import capitalize from 'lodash/capitalize';
import NavigationTabs from 'pages/Dashboard/components/NavigationTabs';
import {
  useNavigate,
  useParams,
} from 'react-router-dom';
import SearchInput from 'pages/Dashboard/components/SearchInput';
import SearchIcon from 'core/icons/search';
import AddIcon from '@mui/icons-material/Add';
import Button from 'pages/Dashboard/components/Button';
import {
  PayerEntryConfigDTO,
} from 'dtos/payerEntryConfigDTO';
import {
  useTableReset,
} from 'core/hooks';
import {
  VirtualTableInstance,
} from 'core/components/VirtualTable/types';
import invert from 'lodash/invert';
import {
  PayerEntryConfigSortKey,
} from 'dtos/payerEntryConfigSortKey';
import {
  PayerEntryConfigTypes,
} from 'dtos/payerEntryConfigTypes';
import {
  SortOrder,
} from 'dtos/sortOrder';
import {
  useSyncSearchQuery,
} from 'pages/Dashboard/hooks/url';
import useStorageState from 'core/hooks/storage';
import SortBy, {
  SortOption,
  SortValue,
} from 'views/EMR/components/SortBy';
import SETTINGS_PATHS from 'views/EMR/Settings/paths';

type PayerConfigSortValue = SortValue<PayerEntryConfigSortKey>;
const defaultPayerConfigSortValue: PayerConfigSortValue = {
  sortBy: PayerEntryConfigSortKey.PayerName,
  sortOrder: SortOrder.Ascending,
};

const payerConfigSortOptions: SortOption<PayerEntryConfigSortKey>[] = [
  {
    label: 'Payer name',
    value: PayerEntryConfigSortKey.PayerName,
    defaultSortOrder: SortOrder.Ascending,
    directionOptions: [
      { label: 'A to Z', value: SortOrder.Ascending },
      { label: 'Z to A', value: SortOrder.Descending },
    ],
  },
  {
    label: 'Pinned',
    value: PayerEntryConfigSortKey.Pinned,
    defaultSortOrder: SortOrder.Descending,
    directionOptions: [
      { label: 'Pinned first', value: SortOrder.Descending },
      { label: 'Unpinned first', value: SortOrder.Ascending },
    ],
  },
];

const payerTypes = ['electronic', 'paper'] as const;
export type PayerType = typeof payerTypes[number];
export type PayerParams = { type: PayerType };
const payerTypeByIndex = invert(payerTypes);

const tabs = payerTypes.map((key) => ({
  key,
  label: capitalize(key),
}));

export default function InsuranceSettings() {
  const { type = 'electronic' } = useParams<PayerParams>();
  const isElectronic = type === 'electronic';

  const navigate = useNavigate();
  const {
    searchQuery,
    setSearchQuery,
  } = useSyncSearchQuery();
  const { tableInstanceRef, reset } = useTableReset();
  const activeIndex = Number(payerTypeByIndex[type]);
  const [sortValue, setSortValue] = useStorageState<PayerConfigSortValue>(
    'payerConfig_sortValue',
    defaultPayerConfigSortValue,
    'local',
  );

  const [
    payerConfigDialogParams,
    setPayerConfigDialogParams,
  ] = useState<PayerConfigDialogParams | null>(null);

  useEffect(() => {
    const unsubscribeFromPayerConfigDialog = settingsEventChannel.on('onPayerConfigDialog', (params) => {
      setPayerConfigDialogParams(params);
    });

    const unsubscribeFromPayerConfigChange = settingsEventChannel.on('onPayerConfigChange', () => {
      tableInstanceRef?.current?.refetch?.();
    });

    return () => {
      unsubscribeFromPayerConfigDialog();
      unsubscribeFromPayerConfigChange();
    };
  }, []);

  const onPayerConfigDialog = useCallback((data?: PayerEntryConfigDTO) => {
    settingsEventChannel.emit('onPayerConfigDialog', {
      data: !isNil(data) ? data : undefined,
      type,
    });
  }, [type]);

  return (
    <div className="h-full w-full flex flex-col gap-4">
      <div className="flex px-4 w-full justify-between">
        <div className="flex gap-4">
          <NavigationTabs
            classes={{
              root: '!min-h-[unset]',
              tab: '!font-semibold !min-w-[unset] !w-fit',
              active: '!border-blue-500 !text-blue-500',
            }}
            tabs={tabs}
            activeIndex={activeIndex}
            onClick={(index) => {
              navigate(`${SETTINGS_PATHS.PAYERS}/${payerTypes[index]}`);
              setSearchQuery('');
            }}
          />
          <SearchInput
            key="payer-search"
            placeholder="Enter Payer Name..."
            className="w-96 h-7"
            value={searchQuery}
            onChange={(value) => {
              setSearchQuery(value);
              reset();
            }}
            inputProps={{ autoComplete: 'off' }}
            icon={<SearchIcon className="!w-4 !h-4 text-gray-400" />}
            id="payer-search"
          />
          <SortBy
            value={sortValue}
            options={payerConfigSortOptions}
            onChange={(nextSortValue) => {
              setSortValue(nextSortValue);
              reset();
            }}
          />
        </div>
        <Button
          className="!h-8"
          variant="contained"
          color="primary"
          type="button"
          startIcon={<AddIcon className="!w-4 !h-4" />}
          onClick={() => { onPayerConfigDialog(); }}
        >
          Add
          {isElectronic ? ' Configuration' : ' Payer'}
        </Button>
      </div>
      <List
        tableRef={tableInstanceRef as RefObject<VirtualTableInstance>}
        params={{
          searchQuery: searchQuery.trim(),
          configType: type as PayerEntryConfigTypes,
          sortBy: sortValue.sortBy,
          sortOrder: sortValue.sortOrder,
        }}
        type={type}
        onPayerConfigDialog={onPayerConfigDialog}
      />
      {!isNil(payerConfigDialogParams) && (
        <CreateInsuranceAddressDialog
          {...payerConfigDialogParams}
          onClose={() => { setPayerConfigDialogParams(null); }}
        />
      )}
    </div>
  );
}
